import { ModalUtils } from './modal';

function findViewRoot(): HTMLElement {

	const dialogs = ModalUtils.getAllIds();
	if (dialogs.length)
		return document.getElementById(`dialog:${dialogs.last()!}:content`)!;

	const appElement = document.getElementById('app');
	if (!appElement)
		throw Error('Cannot activate barcode scanner since no dialog is being shown and #app element could not be found');
	return appElement;
}

// Empieza a leer códigos de barras mientras el foco esté asociado a la vista (página o diálogo/screen) que está
// en primer plano en este momento.
// Devuelve una función que debe ser invocada para detener la lectura de códigos de barras
export function start(onBarcodeRead: (barcode: string) => void): () => void {

	const element = findViewRoot();

	const originalTabIndex = element.getAttribute('tabindex');
	element.setAttribute('tabindex', '-1');

	// Mando el foco para que empiece a leer. Sin el setTimeout no siempre pilla el foco,
	// ¿por qué? No lo sabemos. ¿Por qué 250? Porque parece que funciona con eso.

	// Si el elemento actual que tiene el foco no es hijo de la pantalla, envío el foco al dialogo de la pantalla.
	setTimeout(() => {
		const currentActiveElement = document.activeElement as HTMLElement;
		if (!currentActiveElement || !isParent(element, currentActiveElement))
			element.focus();
	}, 250);

	const listener = new BarcodeListener();
	listener.attach(element, onBarcodeRead);

	return function () {
		if (originalTabIndex)
			element.setAttribute('tabindex', originalTabIndex);

		listener.dettach();
	};
}

function isParent(parent: HTMLElement, child: HTMLElement) {
	let node = child.parentNode;

	// keep iterating unless null
	while (node != null) {
		if (node === parent)
			return true;
		node = node.parentNode;
	}

	return false;
}

class BarcodeListener {

	private element: Nullable<HTMLElement> = null;
	private onBarcodeRead: (barcode: string) => void = _ => { };

	private recording = false;
	private buffer = '';

	attach(element: HTMLElement, onBarcodeRead: typeof this.onBarcodeRead) {
		this.element = element;
		this.onBarcodeRead = onBarcodeRead;

		// Tengo dos tipos de lectores a la hora de mandar datos. Algunos mandan teclas normales,
		// y otros van mandando Alt+ASCII, que genera varios keydown, y al final un keypress.
		// No puedo escuchar sólo el keypress, porque en los que mandan teclas normales, el STX
		// y ETX, que se mandan como Ctrl+B y Ctrl+C no llegan al keypress, sólo al keydown.

		// El plan es escuchar los dos eventos para poder detectar el prefijo/sufijo, pero luego
		// a la hora de acumular en el buffer, ceñirme al keypress.

		this.element.addEventListener('keydown', this.handleKeyEvent);
		this.element.addEventListener('keypress', this.handleKeyEvent);
	}

	dettach() {
		this.element?.removeEventListener('keydown', this.handleKeyEvent);
		this.element?.removeEventListener('keypress', this.handleKeyEvent);
	}

	shouldStartRecording(e: KeyboardEvent): boolean {
		const isBarcodePrefix = e.key === '#' || (e.ctrlKey && e.key === 'b') || (e.ctrlKey && e.key === 'B') || e.key === '\x02';
		return !this.recording && isBarcodePrefix;
	}

	shouldEndRecording(e: KeyboardEvent): boolean {
		const isBarcodeSuffix = e.key === '#' || (e.ctrlKey && e.key === 'c') || (e.ctrlKey && e.key === 'C') || e.key === '\x03';
		return this.recording && isBarcodeSuffix;
	}

	handleKeyEvent = (e: KeyboardEvent) => {

		if (this.shouldStartRecording(e)) {
			this.startRecording(e);
			return;
		}

		if (this.shouldEndRecording(e)) {
			this.endRecording(e);
			return;
		}

		if (this.recording) {
			this.recordEvent(e);
		}
	};

	private startRecording(e: KeyboardEvent) {
		this.recording = true;

		// Evito que se acabe lanzando luego el evento keypress, porque si
		// se están usando # como prefijo y sufijo, en el keypress se pararía
		// automáticamente la lectura
		e.preventDefault();
	}

	private recordEvent(e: KeyboardEvent) {
		if (!e.ctrlKey && !e.metaKey && e.key.length === 1) {
			// Sólo guardo en el keypress. En el caso de los Alt+ASCII, me llegará el keypress con el código traducido.
			// En el caso de los normales, me llegará el keypress con el mismo key que el keydown.
			// En el caso de los mixtos (que envían STX/ETX como Alt+ASCII y resto como Keypress), esperaré a que esté traducido.
			if (e.type === 'keypress') {
				this.buffer += e.key;
				e.preventDefault();
			}
		}
	}

	private endRecording(e: KeyboardEvent) {
		this.recording = false;
		if (this.buffer)
			this.onBarcodeRead(this.buffer);
		this.buffer = '';

		// Evito que se acabe lanzando luego el evento keypress, porque si
		// se están usando # como prefijo y sufijo, en el keypress se volvería a iniciar
		// una lectura que realmente no se está produciendo.
		e.preventDefault();
	}
}
